import { axiosInstance } from "lib/axios";
import { searchAPI } from "services/apis.service";

const requestConfig = { cache: false, forceRefresh: true };

const unwrap = (payload) => {
  if (payload?.success === false) {
    const message = Array.isArray(payload.errors)
      ? payload.errors.map((error) => error?.message || error).join(" ")
      : payload.message || "The request could not be completed.";
    throw new Error(message);
  }
  return payload?.data ?? payload;
};

export const getTeacherSubmissionFolders = async () => {
  const { data } = await axiosInstance.get(
    searchAPI.getSubmissionFoldersForTeacher,
    requestConfig
  );
  return unwrap(data) || [];
};

export const getSubmissionFolderDetail = async (
  submissionFolderID,
  pageIndex = 1,
  pageSize = 50
) => {
  const { data } = await axiosInstance.post(
    searchAPI.getSubmissionFolderDetail,
    { submissionFolderID, pageIndex, pageSize },
    requestConfig
  );
  return unwrap(data);
};

export const createSubmissionFolder = async (payload) => {
  try {
    const { data } = await axiosInstance.post(
      searchAPI.createSubmissionFolder,
      payload,
      requestConfig
    );
    return unwrap(data);
  } catch (error) {
    const payloadError = error.response?.data;
    if (payloadError?.success === false) {
      unwrap(payloadError);
    }
    throw error;
  }
};

export const updateSubmissionFolder = async (payload) => {
  try {
    const { data } = await axiosInstance.put(
      searchAPI.updateSubmissionFolder,
      payload,
      requestConfig
    );
    return unwrap(data);
  } catch (error) {
    const payloadError = error.response?.data;
    if (payloadError?.success === false) {
      unwrap(payloadError);
    }
    throw error;
  }
};

export const archiveSubmissionFolder = async (submissionFolderID, archive = true) => {
  const { data } = await axiosInstance.put(
    searchAPI.archiveSubmissionFolder,
    { submissionFolderID, archive },
    requestConfig
  );
  return unwrap(data);
};

export const deleteSubmissionFolder = async (submissionFolderID) => {
  const { data } = await axiosInstance.delete(
    searchAPI.deleteSubmissionFolder,
    { ...requestConfig, data: { submissionFolderID } }
  );
  return unwrap(data);
};

/**
 * The frozen contents of one submission, captured when its folder was archived.
 * Archived folders no longer read from the student's live collection.
 */
export const getArchiveSnapshotDetail = async (snapshotID) => {
  const { data } = await axiosInstance.post(
    searchAPI.getArchiveSnapshotDetail,
    { snapshotID },
    requestConfig
  );
  return unwrap(data);
};

export const getArchiveWorkCitedDocument = async (archiveWorkCitedId) => {
  const { data } = await axiosInstance.post(
    searchAPI.getSubmissionArchiveWorkCitedDocument,
    { archiveWorkCitedId },
    {
      responseType: "blob",
      cache: false,
    }
  );
  return new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
};

export const getStudentSubmissionFolders = async (distributionListID) => {
  const config = { ...requestConfig };
  if (distributionListID != null) {
    config.params = { distributionListID };
  }
  const { data } = await axiosInstance.get(
    searchAPI.getSubmissionFoldersForStudent,
    config
  );
  return unwrap(data) || [];
};

export const submitCollection = async (collectionID, submissionFolderID) => {
  try {
    const { data } = await axiosInstance.post(
      searchAPI.submitCollection,
      { collectionID, submissionFolderID },
      requestConfig
    );
    return unwrap(data);
  } catch (error) {
    const payload = error.response?.data;
    if (payload?.success === false) {
      unwrap(payload);
    }
    throw error;
  }
};

export const getMySubmissions = async (submissionFolderID = null) => {
  const { data } = await axiosInstance.post(
    searchAPI.getMySubmissions,
    { submissionFolderID },
    requestConfig
  );
  return unwrap(data) || [];
};

export const getSubmissionDetail = async (submissionID) => {
  const { data } = await axiosInstance.post(
    searchAPI.getSubmissionDetail,
    { submissionID },
    requestConfig
  );
  return unwrap(data);
};

/** Resolves the collection (and folder) a submission belongs to from its ID alone. */
export const getSubmissionContext = async (submissionID) => {
  try {
    const { data } = await axiosInstance.post(
      searchAPI.getSubmissionContext,
      { submissionID },
      requestConfig
    );
    return unwrap(data);
  } catch (error) {
    const payload = error.response?.data;
    if (payload?.success === false) {
      unwrap(payload);
    }
    throw error;
  }
};

export const removeSubmission = async (submissionID) => {
  const { data } = await axiosInstance.delete(
    searchAPI.removeSubmission,
    { ...requestConfig, data: { submissionID } }
  );
  return unwrap(data);
};

export const reviewSubmission = async ({
  submissionID,
  isReviewed,
  teacherNote,
  isTeacherNotePublished = true,
}) => {
  const { data } = await axiosInstance.put(
    searchAPI.reviewSubmission,
    {
      submissionID,
      isReviewed,
      submissionStatus: null,
      teacherNote,
      isTeacherNotePublished,
    },
    requestConfig
  );
  return unwrap(data);
};
